import { ReactElement, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Input,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  StatusBar,
  Typography,
} from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { capitalise } from 'src/helpers/capitalise';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { ICloseDuplicateTrayOptions, IDuplicateNameFormValues } from './types';
import styles from './DuplicateNameTrayContent.module.scss';

export const DUPLICATE_NAME_ERROR = 'Name must be unique for the brand';

export interface DuplicateNameTrayContentProps {
  entityLabel: string;
  nameLabel: string;
  sourceName?: string;
  existingNames: string[];
  maxLength?: number;
  errorTag: string;
  openId: number;
  onDuplicate: (name: string) => Promise<void>;
  onClose: (options?: ICloseDuplicateTrayOptions) => void;
}

export const DuplicateNameTrayContent = ({
  entityLabel,
  nameLabel,
  sourceName,
  existingNames,
  maxLength,
  errorTag,
  openId,
  onDuplicate,
  onClose,
}: DuplicateNameTrayContentProps): ReactElement => {
  const { captureException } = useUserTracking();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const capitalisedEntityLabel = capitalise(entityLabel);
  const requiredMessage = `Please enter a ${entityLabel} name`;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<IDuplicateNameFormValues>({
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  useEffect(() => {
    reset({ name: '' });
    clearError();
  }, [openId]);

  const onSubmit = async (values: IDuplicateNameFormValues) => {
    clearError();

    try {
      await onDuplicate(values.name.trim());

      showNotification({ status: SnackbarStatus.SUCCESS, message: `${capitalisedEntityLabel} duplicated` });
      onClose({ duplicated: true });
    } catch (error) {
      captureException(error, { tags: { action: errorTag } });
      const message = error instanceof Error ? error.message : undefined;
      showNotification({
        status: SnackbarStatus.ERROR,
        message: message || `Failed to duplicate ${entityLabel}. Please try again.`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['content']}>
      <div>
        {sourceName && (
          <Typography size="medium" weight="regular" className={styles['subtitle']}>
            {capitalisedEntityLabel} to be duplicated - <strong>{sourceName}</strong>
          </Typography>
        )}

        <Input
          type="text"
          label={nameLabel}
          register={register('name', {
            required: requiredMessage,
            maxLength: maxLength && {
              value: maxLength,
              message: `${capitalisedEntityLabel} name must be ${maxLength} characters or fewer`,
            },
            validate: {
              notEmpty: (value) => value.trim().length > 0 || requiredMessage,
              isUnique: (value) => {
                const trimmedValue = value.trim().toLocaleLowerCase();
                const isDuplicate = existingNames.some((name) => name.toLocaleLowerCase() === trimmedValue);
                return !isDuplicate || DUPLICATE_NAME_ERROR;
              },
            },
          })}
          error={errors?.name?.message}
        />
      </div>

      {errorMessage && <StatusBar message={errorMessage} variant="error" />}

      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={() => onClose()}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton type="submit" size="full-width" loading={isSubmitting} disabled={isSubmitting || !isValid}>
              Duplicate
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

export default DuplicateNameTrayContent;
